import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { SlaBusinessQuestionnaire } from 'src/app/models/documents.model';
import { FormValidationService } from 'src/app/services/form-validation.service';
import { setRegex } from 'src/app/libs/functions/common.functions';

@Component({
  selector: 'app-business-questionaire-input',
  standalone: false,
  templateUrl: './business-questionaire-input.component.html',
  styleUrl: './business-questionaire-input.component.scss'
})
export class BusinessQuestionaireInputComponent {
  [key: string]: any;
  setRegex = setRegex;
  businessForm!: FormGroup;
  current: number = 2;
  radioValue: string = '1';
  @Input() slaDataInput: SlaBusinessQuestionnaire = {
    has_business_entity_deed: false,
    has_taxable_entrepreneur_certificate: false,
    has_taxpayer_identification_number: false,
    is_sla_under_power_of_attorney: false,
    has_authorization_ktp_or_kitas: false,
    has_business_identification_number: false,
    authorization_holder_name: '',
    authorization_holder_email: '',
    authorization_holder_job_title: '',
    authorization_holder_address: '',
    authorization_holder_phone: '',
  };
  @Output() businessData = new EventEmitter<SlaBusinessQuestionnaire>();
  errMessEmail: string = '';
  validEmail: boolean = true;
  errMessBName: string = '';
  validBName: boolean = true;
  errMessJName: string = '';
  validJName: boolean = true;
  validNumber: boolean = true;
  errMessNumber: string = '';
  validAddress: boolean = true;
  errMessAddress: string = '';
  validTax: boolean = true;
  errMessTax: string = '';
  validNPWP: boolean = true;
  errMessNPWP: string = '';
  validNIB: boolean = true;
  errMessNIB: string = '';
  serviceType: string = localStorage.getItem('service_type_doc') || '';
  mode: string = localStorage.getItem('mode-sla-input') || '';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private formValidation: FormValidationService,
    private message: NzMessageService
  ) { }

  ngOnInit() {
    this.initForm();

    if (this.businessForm) {
      this.businessForm.patchValue(this.slaDataInput);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
      if (changes['slaDataInput'] && changes['slaDataInput'].currentValue) {
        const slaData = changes['slaDataInput'].currentValue;

        if (this.businessForm) {
          this.businessForm.patchValue(slaData);
        }
      }
    }

    initForm() {
      this.businessForm = this.fb.group({
        has_business_entity_deed: [false],
        has_taxable_entrepreneur_certificate: [false],
        taxable_certificate_number: [{ value: '', disabled: true }, [Validators.required, Validators.pattern('^\\d{15}$')]],
        has_taxpayer_identification_number: [false],
        taxpayer_number: [{ value: '', disabled: true }, [Validators.required, Validators.minLength(15),
          Validators.maxLength(16),
          Validators.pattern('^\\d{15,16}$')]],
        is_sla_under_power_of_attorney: [false],
        has_authorization_ktp_or_kitas: [false],
        has_business_identification_number: [false],
        business_identification_number: [{ value: '', disabled: true }, [Validators.required, Validators.pattern('^\\d{13}$')]],
        authorization_holder_name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
        authorization_holder_email: ['', [Validators.required, Validators.email]],
        authorization_holder_job_title: ['', Validators.required],
        authorization_holder_address: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(100)]],
        authorization_holder_phone: ['', [Validators.required, Validators.pattern('^[0-9]*\\.?[0-9]*$')]]
      });

      this.setupConditionalValidation('has_taxable_entrepreneur_certificate', 'taxable_certificate_number', '^\\d{15}$', 'errMessTax', 'validTax');
      this.setupConditionalValidation('has_taxpayer_identification_number', 'taxpayer_number', '^\\d{15,16}$', 'errMessNPWP', 'validNPWP');
      this.setupConditionalValidation('has_business_identification_number', 'business_identification_number', '^\\d{13}$', 'errMessNIB', 'validNIB');
    }

    setupConditionalValidation(toggleField: string, targetField: string, pattern: string, errorField: string, validField: string) {
      this.businessForm.get(toggleField)?.valueChanges.subscribe(value => {
        const control = this.businessForm.get(targetField);
        if (value) {
          control?.setValidators([Validators.required, Validators.pattern(pattern)]);
          control?.enable();
        } else {
          control?.setValue('');
          control?.clearValidators();
          control?.disable();
          (this as any)[errorField] = '';
          (this as any)[validField] = true;
        }
        control?.updateValueAndValidity();
      });
    }

  preventNonNumeric(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;

    if (
      !/^\d$/.test(event.key) &&
      !['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab'].includes(
        event.key
      )
    ) {
      event.preventDefault();
    }

    if (input.value.length === 0 && event.key !== '8') {
      event.preventDefault();
    }
  }
  preventNonNumericTax(event: KeyboardEvent): void {if (
      !/^\d$/.test(event.key) &&
      !['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab'].includes(
        event.key
      )
    ) {
      event.preventDefault();
    }
  }

  validateField(event: Event, toggleField: string, targetField: string, minLength: number, maxLength: number, errorField: string, validField: string, errorMessage: string) {
    const input = event.target as HTMLInputElement;
    const hasCertificate = this.businessForm.get(toggleField)?.value;

    if (!hasCertificate) {
      (this as any)[errorField] = '';
      (this as any)[validField] = true;
      return;
    }

    if (input.value.length === 0) {
      (this as any)[validField] = false;
      (this as any)[errorField] = `${errorMessage} must be filled`;
    } else if (!/^\d+$/.test(input.value)) {
      (this as any)[validField] = false;
      (this as any)[errorField] = `${errorMessage} must contain only digits.`;
    } else if (input.value.length < minLength) {
      (this as any)[validField] = false;
      (this as any)[errorField] = `${errorMessage} must be at least ${minLength} digits.`;
    } else if (input.value.length > maxLength) {
      (this as any)[validField] = false;
      (this as any)[errorField] = `${errorMessage} must be at most ${maxLength} digits.`;
    } else {
      (this as any)[validField] = true;
      (this as any)[errorField] = '';
    }
  }

  validateTax(event: Event) {
    this.validateField(event, 'has_taxable_entrepreneur_certificate', 'taxable_certificate_number', 15, 15, 'errMessTax', 'validTax', 'SPPKP number');
  }

  validateNPWP(event: Event) {
    this.validateField(event, 'has_taxpayer_identification_number', 'taxpayer_number', 15, 16, 'errMessNPWP', 'validNPWP', 'Taxpayer Identification Number');
  }

  validateNIB(event: Event) {
    this.validateField(event, 'has_business_identification_number', 'business_identification_number', 13, 13, 'errMessNIB', 'validNIB', 'Business Identification Number');
  }


  validateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const result = this.formValidation.validatePhoneNumber(input.value)

    this.validNumber = result.valid;
    this.errMessNumber = result.message;
  }


  validateEmail() {
    const value = this.businessForm.get('authorization_holder_email')?.value || '';

    const result = this.formValidation.validateEmail(value)
    this.validEmail = result.valid;
    this.errMessEmail = result.message;
}

  setRegexBName(event: KeyboardEvent) {
    const regex = /^[a-zA-Z .-]+$/g;
    return regex.test(event.key);
  }

  validateInputField(event: Event, fieldType: 'BName' | 'JName') {
    const input = event.target as HTMLInputElement;
    const minLength = 3;
    const maxLength = 50;
    const fieldValidKey = `valid${fieldType}`;
    const fieldErrorKey = `errMess${fieldType}`;

    if (input.value === '') {
      this[fieldValidKey] = false;
      this[fieldErrorKey] = `Authorization Holder ${fieldType === 'BName' ? 'Name' : 'Job'} must be filled in`;
    } else if (input.value.length < minLength) {
      this[fieldValidKey] = false;
      this[fieldErrorKey] = `Minimum ${minLength} characters`;
    } else if (input.value.length > maxLength) {
      this[fieldValidKey] = false;
      this[fieldErrorKey] = `Maximum ${maxLength} characters`;
    } else {
      this[fieldValidKey] = true;
      this[fieldErrorKey] = '';
    }
  }


  validateAddress(event: Event) {
    const input = event.target as HTMLInputElement;

    if (input.value.length === 0) {
      this.validAddress = false;
      this.errMessAddress = 'Address cannot be empty';
    } else if (input.value.length > 0 && input.value.length < 10) {
      this.validAddress = false;
      this.errMessAddress = 'Minimum 10 characters';
    } else if (input.value.length > 100) {
      this.validAddress = false;
      this.errMessAddress = 'Maximum 100 characters';
    } else {
      this.validAddress = true;
      this.errMessAddress = '';
    }
  }

  onBack(): void {
    if (this.current > 1) {
      this.current -= 1;
      this.router.navigate(['/document/sla-data-input'], { queryParams: { step: 1, service_type: this.serviceType } });
    }
  }
  onSubmit(): void {
    if (this.businessForm.valid) {
      this.current = 2;
      const formValue = {
        ...this.businessForm.value,
        taxable_certificate_number: this.businessForm.value.taxable_certificate_number ? String(this.businessForm.value.taxable_certificate_number) : '',
        taxpayer_number: this.businessForm.value.taxpayer_number ? String(this.businessForm.value.taxpayer_number) : '',
        business_identification_number: this.businessForm.value.business_identification_number ? String(this.businessForm.value.business_identification_number) : ''
      };
        this.businessData.emit(formValue);
        if (this.mode === 'create') {
          this.router.navigate(['/document/sla-data-input'], {
            queryParams: { step: 3, service_type: this.serviceType },
          });
        } else {
          this.router.navigate(['/document/sla-data-input'], {
            queryParams: { step: 3, mode: this.mode, service_type: this.serviceType },
          });
        }
    } else {
      this.message.create('error', 'Please fill in all the required fields');
    }
  }

  get isFormInvalid(): boolean {
    return (
      this.businessForm.invalid ||
      this.errMessEmail !== '' ||
      this.errMessBName !== '' ||
      this.errMessAddress !== '' ||
      this.errMessNumber !== ''
    );
  }
}
